/**
 * Surcharge pricing engine — line-for-line TS port of the canonical Python
 * reference implementation in `order-service/business_logic/surcharge_logic.py`
 * (see `docs/superpowers/plans/2026-07-06-surcharge-module.md`, "Canonical
 * Algorithm"). Any change here must stay behavior-identical to that Python
 * source and to the sibling ports in pos_v2 / tappya-dashboard-app — all are
 * verified against the SAME shared vector fixture
 * (`docs/superpowers/specs/2026-07-06-surcharge-test-vectors.json`, copied
 * byte-identical into `src/lib/__tests__/surcharge_test_vectors.json`).
 *
 * Branch timezone: this app has no branch-timezone handling anywhere (see
 * `promotion-window.ts`'s `isPromotionWithinWindow`, which also uses the
 * customer's local `Date` clock and documents this as a display-side
 * estimate — the backend recomputes authoritatively against the branch
 * timezone). We follow that existing precedent here: `ctx.localDateTime` is
 * expected to already be "branch-local" by convention, but in practice the
 * only value ever passed by this app is `new Date()` (device local time).
 */

import type { LocalizedString } from "@/api/online-ordering-service/model/localizedString";

/** 2-decimal HALF-UP rounding, matching Python's `Decimal(...).quantize(ROUND_HALF_UP)`.
 * All amounts in this domain are non-negative, so plain `Math.round` (which
 * rounds half-away-from-zero for positive numbers, i.e. half-up) matches. */
export function round2(x: number): number {
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

export type SurchargeSchedule = {
  daysOfWeek?: number[] | null; // 0=Mon..6=Sun; empty/None = all days
  startTime?: string | null; // "HH:MM" branch-local
  endTime?: string | null; // "HH:MM" branch-local; startTime > endTime = overnight window
};

export type SurchargeDefinition = {
  id: string;
  isActive: boolean;
  name: LocalizedString | Record<string, string>;
  type: "fixed" | "percentage";
  amount: number;
  percentageBase?: "pre_discount" | "post_discount";
  taxIds?: string[] | null;
  waivable?: boolean;
  applicableBranchIds?: string[] | null;
  applicableOrderTypes?: string[] | null;
  applicableOrderSources?: string[] | null;
  schedule?: SurchargeSchedule | null;
  validityStart?: string | null; // "YYYY-MM-DD" (or ISO datetime; first 10 chars used)
  validityEnd?: string | null;
  minOrderValue?: number | null;
};

export type TaxRef = {
  id: string;
  name?: LocalizedString | Record<string, string>;
  rate: number; // percent, e.g. 14.0
  isIncludedInPrice?: boolean;
};

export type SurchargeContext = {
  branchId: string;
  orderType: string;
  orderSource: string;
  /** Branch-local datetime (device local time — see module doc). */
  localDateTime: Date;
  itemsSubtotal: number;
  discountTotal: number;
  /** Till plan 2.6 (O8): % surcharge base after reductions, on the goods' VAT-net. Omitted = gross after discounts. */
  postDiscountNetBase?: number;
  /** Till plan 2.6: `pre_discount` % base on the goods' VAT-net. Omitted = itemsSubtotal. */
  preDiscountNetBase?: number;
};

export type AppliedSurchargeTax = {
  taxId: string;
  taxName?: LocalizedString | Record<string, string> | null;
  ratePercent: number;
  taxableAmount: number;
  calculatedAmount: number;
  isIncluded: boolean;
};

export type AppliedSurcharge = {
  surchargeId: string;
  name: LocalizedString | Record<string, string>;
  type: "fixed" | "percentage";
  baseAmount: number;
  amount: number;
  taxes: AppliedSurchargeTax[];
  waived: boolean;
  waivedBy: string | null;
};

/** "YYYY-MM-DD" (branch-local) from a Date, taking just the date part. */
function dateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "HH:MM" (branch-local) from a Date. */
function hhmm(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

/** Python `datetime.weekday()`: 0=Monday .. 6=Sunday. JS `Date.getDay()` is
 * 0=Sunday..6=Saturday, so convert with `(getDay() + 6) % 7`. */
function isoWeekday(d: Date): number {
  return (d.getDay() + 6) % 7;
}

export function surchargeMatches(s: SurchargeDefinition, ctx: SurchargeContext): boolean {
  if (!s.isActive) return false;

  const d = dateOnly(ctx.localDateTime);
  if (s.validityStart && d < String(s.validityStart).slice(0, 10)) return false;
  if (s.validityEnd && d > String(s.validityEnd).slice(0, 10)) return false;

  const sched = s.schedule;
  if (sched) {
    const dows = sched.daysOfWeek ?? [];
    if (dows.length > 0 && !dows.includes(isoWeekday(ctx.localDateTime))) return false;

    const hm = hhmm(ctx.localDateTime);
    const start = sched.startTime;
    const end = sched.endTime;
    if (start && end && start > end) {
      // overnight window e.g. 22:00-03:00
      if (!(hm >= start || hm < end)) return false;
    } else {
      if (start && hm < start) return false;
      if (end && hm >= end) return false;
    }
  }

  const checks: Array<[string[] | null | undefined, string]> = [
    [s.applicableBranchIds, ctx.branchId],
    [s.applicableOrderTypes, ctx.orderType],
    [s.applicableOrderSources, ctx.orderSource],
  ];
  for (const [allowed, key] of checks) {
    if (allowed && allowed.length > 0 && !allowed.includes(key)) return false;
  }

  const postDiscount = Math.max(0, ctx.itemsSubtotal - ctx.discountTotal);
  if (s.minOrderValue != null && postDiscount < s.minOrderValue) return false;

  return true;
}

export function computeAppliedSurcharges(
  definitions: ReadonlyArray<SurchargeDefinition>,
  ctx: SurchargeContext,
  taxesById: Record<string, TaxRef>,
): AppliedSurcharge[] {
  const applied: AppliedSurcharge[] = [];
  const postDiscount = Math.max(0, ctx.itemsSubtotal - ctx.discountTotal);

  for (const s of definitions) {
    if (!surchargeMatches(s, ctx)) continue;

    let base: number;
    let amount: number;
    if (s.type === "fixed") {
      base = s.amount;
      amount = round2(s.amount);
    } else {
      base =
        s.percentageBase === "pre_discount"
          ? (ctx.preDiscountNetBase ?? ctx.itemsSubtotal)
          : (ctx.postDiscountNetBase ?? postDiscount);
      amount = round2((base * s.amount) / 100);
    }

    const taxInfos: AppliedSurchargeTax[] = [];
    for (const tid of s.taxIds ?? []) {
      const tax = taxesById[tid];
      if (!tax) continue;
      const rate = tax.rate / 100;
      let taxable: number;
      let calc: number;
      if (tax.isIncludedInPrice) {
        taxable = round2(amount / (1 + rate));
        calc = round2(amount - taxable);
      } else {
        taxable = amount;
        calc = round2(amount * rate);
      }
      taxInfos.push({
        taxId: tid,
        taxName: tax.name ?? null,
        ratePercent: tax.rate,
        taxableAmount: taxable,
        calculatedAmount: calc,
        isIncluded: !!tax.isIncludedInPrice,
      });
    }

    applied.push({
      surchargeId: s.id,
      name: s.name,
      type: s.type,
      baseAmount: round2(base),
      amount,
      taxes: taxInfos,
      waived: false,
      waivedBy: null,
    });
  }

  return applied;
}
